import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { tokens } from "../tokens";
import { useBreakpoint } from "../hooks/useBreakpoint";

/* ── Brand constants ── */
const GREEN      = "#1e9254";
const GREEN_DARK = "#155c36";
const AMBER      = "#D97706";
const STAR_GOLD  = "#F0A93C";
const SERIF      = "'Instrument Serif', Georgia, serif";
const SANS       = tokens?.font?.family?.sans || "'DM Sans', -apple-system, sans-serif";
const BORDER     = "#E5E7EB";
const FAINT      = "#8a93a8";
const SECTION_BG = "#F7F8FA"; // matches ProFeed's T.bg exactly
const GREEN_LIGHT = "#7CDB9F"; // same light-green accent used for "Jirani" in the hero headline
/* Same dark radial gradient as HeroTopSection / TreatYourself's background */
const HERO_BG = "radial-gradient(ellipse at 30% 15%, #1e4a2e 0%, #163322 45%, #0d2e1a 100%)";

if (typeof document !== "undefined" && !document.getElementById("__jirani_deals_fonts")) {
  const l = document.createElement("link");
  l.id = "__jirani_deals_fonts";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap";
  document.head.appendChild(l);
}

/* ── Image pools per category (swap for real pro photos from Supabase) ── */
const IMG = {
  cleaning:   ["https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=340&h=280&fit=crop"],
  plumbing:   ["https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=340&h=280&fit=crop"],
  electrical: ["https://images.unsplash.com/photo-1544724569-5f546fd6f2b5?w=340&h=280&fit=crop"],
  hair:       ["https://images.unsplash.com/photo-1560066984-138dadb4c035?w=340&h=280&fit=crop"],
  tutoring:   ["https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=340&h=280&fit=crop"],
  photography:["https://images.unsplash.com/photo-1452587925148-ce544e77e70d?w=340&h=280&fit=crop"],
  gardening:  ["https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=340&h=280&fit=crop"],
  carpentry:  ["https://images.unsplash.com/photo-1504148455328-c376907d081c?w=340&h=280&fit=crop"],
  errands:    ["https://images.unsplash.com/photo-1526367790999-0150786686a2?w=340&h=280&fit=crop"],
  chef:       ["https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=340&h=280&fit=crop"],
  electronics:["https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=340&h=280&fit=crop"],
};

const LOCATIONS = [
  "Nyali", "Westlands", "Bamburi", "Kilimani", "Tudor", "Kasarani",
  "Likoni", "Embakasi", "Mtwapa", "Lang'ata", "Shanzu", "South B",
  "Nakuru", "Kisumu", "Naivasha", "Milimani", "Thika", "Juja", "Meru", "Eldoret",
];

/* Two separate, non-overlapping name pools — neither reuses the six names
   already shown in the hero "On JiraniApp Right Now" section (Wanjiru,
   Brian, Kevin, Faith, Amina, Dennis), and the two rows never share a name. */
const GENERAL_NAMES = ["Grace", "James", "Hassan", "Peter", "Mercy", "Tom", "Aisha", "Sandra", "Priya", "Miriam",
  "Stella", "Eric", "Caroline", "Joseph", "Lucy", "Samuel", "Esther", "David", "Winnie", "Fatuma"];

const PLUMBER_NAMES = ["Josephine", "Cynthia", "Abdalla", "Consolata", "Victor", "Neema", "Charles", "Zainab", "Kelvin", "Purity",
  "Omar", "Rehema", "Anthony", "Judy", "Nicholas", "Salma", "Patrick", "Doreen", "Yusuf", "Beatrice"];

/* Four fresh, non-overlapping name pools for the newly-split rows below —
   checked against every name already used across the homepage (hero strip,
   testimonials, GENERAL_NAMES, PLUMBER_NAMES, and every pool in
   ForYourHousehold, TreatYourself, and DeliveryMovingAndMore), and against
   each other. */
const ELECTRICIAN_NAMES = ["Munyao", "Wanjira", "Kiplimo", "Chepkorir", "Kirui", "Rono", "Sigei", "Jepkemboi", "Chepngetich", "Cheptoo",
  "Kimaiyo", "Bett", "Rotich", "Sang", "Tanui", "Kiptanui", "Tuwei", "Kones", "Yego", "Chumo"];

const GARDENER_NAMES = ["Kipruto", "Biwott", "Kipyego", "Kosgei", "Cherop", "Chelimo", "Jepkosgei", "Jepchumba", "Kimutai", "Koech",
  "Langat", "Barno", "Njue", "Mugo", "Wachira", "Waititu", "Kang'ethe", "Gichuru", "Njihia", "Gathoni"];

const CARPENTER_NAMES = ["Wangui", "Nyawira", "Waceke", "Nyokabi", "Gakii", "Ciku", "Kagwe", "Muriithi", "Kabue", "Nderitu",
  "Mundia", "Mwaura", "Wainaina", "Kabogo", "Thuo", "Kimemia", "Gaitho", "Kariba", "Thande", "Wangoi"];

const ELECTRONICS_NAMES = ["Njiraini", "Gachanja", "Kamotho", "Mwatha", "Kariithi", "Maranga", "Ombati", "Osiako", "Ondari", "Momanyi",
  "Onchari", "Onsomu", "Angwenyi", "Mogaka", "Kerandi", "Nyaera", "Bosire", "Onyancha", "Machogu", "Oigara"];

function makeName(namePool, i) {
  return `${namePool[i % namePool.length]} ${String.fromCharCode(65 + (i % 26))}.`;
}

/* Controlled jobs-count distribution per 20-card row: 3 Beginner, 10 Junior,
   5 Senior, 2 Business Pro — most values sit under 70. Rotated per row
   (via idOffset) so rows don't all show the exact same numbers. */
const JOBS_PATTERN = [8, 14, 19, 27, 31, 35, 38, 41, 44, 46, 29, 33, 48, 52, 58, 61, 65, 68, 110, 130];

function buildRow(categoryList, count, priceRange, namePool, idOffset) {
  const rotate = Math.floor(idOffset / 100);
  return Array.from({ length: count }, (_, i) => {
    const cat = categoryList[i % categoryList.length];
    const discount = [10, 12, 15, 18, 20, 25, 30][i % 7];
    const original = priceRange[0] + ((i * 137) % (priceRange[1] - priceRange[0]));
    const roundedOriginal = Math.round(original / 50) * 50;
    const current = Math.round((roundedOriginal * (1 - discount / 100)) / 10) * 10;
    return {
      id: idOffset + i + 1,
      name: makeName(namePool, i),
      category: cat.label,
      location: LOCATIONS[i % LOCATIONS.length],
      image: IMG[cat.key][i % IMG[cat.key].length],
      discount,
      original: roundedOriginal,
      current,
      rating: (4.6 + ((i * 7) % 5) / 10).toFixed(1),
      jobs: JOBS_PATTERN[(i + rotate) % JOBS_PATTERN.length],
    };
  });
}

/* Single-category variant, used for the five dedicated "Home fixes before
   the rains" sub-rows below (Plumbers, Electricians, Gardeners, Carpenters,
   Electronics Technicians) — same shape as buildRow but takes one category
   key/label directly instead of cycling through a list. */
function buildCategoryRow(categoryKey, categoryLabel, count, priceRange, namePool, idOffset) {
  const rotate = Math.floor(idOffset / 100);
  return Array.from({ length: count }, (_, i) => {
    const discount = [10, 12, 15, 18, 20, 25, 30][i % 7];
    const original = priceRange[0] + ((i * 137) % (priceRange[1] - priceRange[0]));
    const roundedOriginal = Math.round(original / 50) * 50;
    const current = Math.round((roundedOriginal * (1 - discount / 100)) / 10) * 10;
    return {
      id: idOffset + i + 1,
      name: makeName(namePool, i),
      category: categoryLabel,
      location: LOCATIONS[i % LOCATIONS.length],
      image: IMG[categoryKey][i % IMG[categoryKey].length],
      discount,
      original: roundedOriginal,
      current,
      rating: (4.6 + ((i * 7) % 5) / 10).toFixed(1),
      jobs: JOBS_PATTERN[(i + rotate) % JOBS_PATTERN.length],
    };
  });
}

const GENERAL_CATS = [
  { key: "cleaning", label: "Cleaning & Laundry" },
  { key: "hair", label: "Hair & Beauty" },
  { key: "errands", label: "Errands & Delivery" },
  { key: "tutoring", label: "Home Tutoring" },
  { key: "photography", label: "Photography" },
  { key: "chef", label: "Personal Chef" },
];

/* Lowered so the discounted "From" price mostly lands in the KSh 200-500 band */
const GENERAL_DEALS = buildRow(GENERAL_CATS, 20, [300, 700], GENERAL_NAMES, 0);

/* "Home fixes before the rains" is now five dedicated sub-rows instead of
   one mixed row, each with its own subheading. */
const PLUMBER_DEALS     = buildCategoryRow("plumbing", "Plumbing", 20, [350, 800], PLUMBER_NAMES, 100);
const ELECTRICIAN_DEALS = buildCategoryRow("electrical", "Electrical", 20, [350, 800], ELECTRICIAN_NAMES, 200);
const GARDENER_DEALS    = buildCategoryRow("gardening", "Gardening", 20, [300, 700], GARDENER_NAMES, 300);
const CARPENTER_DEALS   = buildCategoryRow("carpentry", "Carpentry & Furniture", 20, [400, 900], CARPENTER_NAMES, 400);
const ELECTRONICS_DEALS = buildCategoryRow("electronics", "Electronics Repair", 20, [350, 800], ELECTRONICS_NAMES, 500);

function fmt(n) {
  return `KSh ${n.toLocaleString("en-KE")}`;
}

/* Level bands are based on jobs completed:
   Beginner < 25, Junior 25-49, Senior 50-99, Business Pro 100+ */
const LEVELS = {
  beginner:    { label: "Beginner",     bg: "#F1F2F5", color: "#4a5268", border: "#E5E7EB", star: false },
  junior:      { label: "Junior",       bg: "#EAF1FB", color: "#2563AA", border: "#BFDBFE", star: false },
  senior:      { label: "Senior",       bg: "#eef8f3", color: GREEN_DARK, border: "#a7d9bc", star: false },
  businessPro: { label: "Business Pro", bg: AMBER,     color: "#fff",    border: AMBER,     star: true  },
};

function getLevel(jobs) {
  if (jobs >= 100) return LEVELS.businessPro;
  if (jobs >= 50) return LEVELS.senior;
  if (jobs >= 25) return LEVELS.junior;
  return LEVELS.beginner;
}

function Avatar({ src, name, style }) {
  const [err, setErr] = useState(false);
  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  if (err || !src) {
    return (
      <div style={{ ...style, background: "#eef8f3", display: "flex", alignItems: "center", justifyContent: "center", color: GREEN_DARK, fontWeight: 700, fontFamily: SANS }}>
        {initials}
      </div>
    );
  }
  return <img src={src} alt={name} style={style} onError={() => setErr(true)} />;
}

function DealCard({ deal, onBook }) {
  const [hovered, setHovered] = useState(false);
  const level = getLevel(deal.jobs);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onBook(deal)}
      style={{
        width: 158, flexShrink: 0, borderRadius: 14, background: "#fff",
        border: `1px solid ${hovered ? "#a7d9bc" : BORDER}`,
        boxShadow: hovered ? "0 10px 26px rgba(0,0,0,.08)" : "none",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        transition: "all .18s ease", overflow: "hidden", cursor: "pointer",
      }}
    >
      <div style={{ position: "relative", height: 120, background: "#eef1ee" }}>
        <Avatar src={deal.image} name={deal.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        <div style={{ position: "absolute", top: 7, left: 7, background: "#fff", borderRadius: 6, padding: "3px 7px", fontSize: 9.5, fontWeight: 500, color: GREEN_DARK, border: "1px solid #a7d9bc", fontFamily: SANS }}>
          Verified
        </div>
        <div style={{ position: "absolute", top: 7, right: 7, background: AMBER, borderRadius: 6, padding: "3px 7px", fontSize: 9.5, fontWeight: 500, color: "#fff", fontFamily: SANS }}>
          {deal.discount}% off
        </div>
      </div>
      <div style={{ padding: "9px 10px 11px" }}>
        <p style={{ margin: 0, fontSize: 12.5, fontWeight: 500, color: "#0f1117", fontFamily: SANS, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {deal.name}
        </p>
        <p style={{ margin: "2px 0 0", fontSize: 10.5, color: FAINT, fontFamily: SANS, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {deal.category} · {deal.location}
        </p>
        <div style={{ margin: "5px 0 0" }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 3,
            fontSize: 9, fontWeight: 600, color: level.color,
            background: level.bg, border: `1px solid ${level.border}`,
            borderRadius: 5, padding: "2px 6px", fontFamily: SANS,
          }}>
            {level.star && (
              <svg width="7" height="7" viewBox="0 0 20 20" fill="#fff" style={{ flexShrink: 0 }}>
                <path d="M10 1.2l2.7 5.7 6.2.6-4.6 4.2 1.3 6.1L10 14.6l-5.6 3.2 1.3-6.1L1.1 7.5l6.2-.6z" />
              </svg>
            )}
            {level.label}
          </span>
        </div>
        <div style={{ margin: "5px 0 0", display: "flex", alignItems: "center", gap: 4 }}>
          <svg width="10" height="10" viewBox="0 0 20 20" fill={STAR_GOLD} style={{ flexShrink: 0 }}>
            <path d="M10 1.2l2.7 5.7 6.2.6-4.6 4.2 1.3 6.1L10 14.6l-5.6 3.2 1.3-6.1L1.1 7.5l6.2-.6z" />
          </svg>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: "#B8791A", fontFamily: SANS }}>{deal.rating}</span>
          <span style={{ fontSize: 10, color: FAINT, fontFamily: SANS }}>·</span>
          <span style={{ fontSize: 10.5, fontWeight: 600, color: GREEN_DARK, fontFamily: SANS }}>{deal.jobs} jobs</span>
        </div>
        <div style={{ margin: "6px 0 0", display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#0f1117", fontFamily: SANS }}>From {fmt(deal.current)}</span>
          <span style={{ fontSize: 11, color: "#9CA3AF", textDecoration: "line-through", fontFamily: SANS }}>{fmt(deal.original)}</span>
        </div>
      </div>
    </div>
  );
}

/* ── Book button — its own element below the card, not inside it,
       but pinned to the same 158px width. ── */
function BookButton({ deal, inCart, onToggleCart }) {
  return (
    <button
      onClick={() => onToggleCart(deal)}
      style={{
        marginTop: 8, width: 158, height: 32, borderRadius: 8,
        border: `1.5px solid ${GREEN}`,
        background: inCart ? "#fff" : GREEN,
        color: inCart ? GREEN_DARK : "#fff",
        fontFamily: SERIF, fontSize: 13.5, fontWeight: 500,
        cursor: "pointer", transition: "all .15s ease", flexShrink: 0,
      }}
    >
      {inCart ? "Booked ✓" : "Book"}
    </button>
  );
}

/* ── Row — header sits in a padded column, track is full width and
       bleeds past it on the right (same pattern as PopularServices).
       No edge fade — that was the thing being removed. ── */
function DealsRow({ title, deals, onBook, cartIds, onToggleCart, padX, marginBottom = 30, dark = false }) {
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", update, { passive: true });
    update();
    return () => el.removeEventListener("scroll", update);
  }, [update, deals]);

  const scroll = (dir) => scrollRef.current?.scrollBy({ left: dir * 480, behavior: "smooth" });
  const arrowFn = dark ? arrowStyleDark : arrowStyle;

  return (
    <div style={{ marginBottom }}>
      <div style={{ width: "100%", padding: `0 ${padX}px` }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 18, gap: 16 }}>
          <h3 style={{ fontFamily: SERIF, fontSize: "clamp(28px,4vw,38px)", fontWeight: 400, color: dark ? "#fff" : "#0f1117", lineHeight: 1.08, letterSpacing: "-0.03em", margin: 0 }}>
            {title}
          </h3>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => scroll(-1)} disabled={!canLeft} style={arrowFn(canLeft)} aria-label="Scroll left">‹</button>
            <button onClick={() => scroll(1)} disabled={!canRight} style={arrowFn(canRight)} aria-label="Scroll right">›</button>
          </div>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="jd-track"
        style={{ overflowX: "auto", paddingBottom: 4, paddingLeft: padX, paddingRight: padX, width: "100%" }}
      >
        <div style={{ display: "flex", gap: 12, width: "max-content" }}>
          {deals.map(deal => (
            <div key={deal.id} style={{ display: "flex", flexDirection: "column", flexShrink: 0 }}>
              <DealCard deal={deal} onBook={onBook} />
              <BookButton deal={deal} inCart={cartIds.has(deal.id)} onToggleCart={onToggleCart} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function arrowStyle(enabled) {
  return {
    width: 32, height: 32, borderRadius: "50%", border: `1.5px solid ${BORDER}`,
    background: "#fff", fontSize: 16, color: "#2d3549", cursor: enabled ? "pointer" : "default",
    opacity: enabled ? 1 : 0.3, lineHeight: 1,
  };
}

/* Translucent-white variant for rows sitting on the dark hero-gradient
   background (Plumbers, Electricians, Gardeners), matching the arrows
   used in TreatYourself.jsx and the hero's own category-pill scroller. */
function arrowStyleDark(enabled) {
  return {
    width: 32, height: 32, borderRadius: "50%",
    border: "1.5px solid rgba(255,255,255,0.38)",
    background: "rgba(255,255,255,0.14)",
    backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
    fontSize: 16, color: "#fff", cursor: enabled ? "pointer" : "default",
    opacity: enabled ? 1 : 0.3, lineHeight: 1,
  };
}

export default function JiraniDeals() {
  const navigate = useNavigate();
  const { isMobile, isTablet } = useBreakpoint();
  const padX = isMobile ? 16 : isTablet ? 20 : 32;
  const onBook = useCallback((deal) => navigate(`/pro/${deal.id}?book=1&promo=1`), [navigate]);

  // Standalone cart state for now — swap this out for your real cart
  // context/store later; onToggleCart is the only thing that needs rewiring.
  const [cartIds, setCartIds] = useState(() => new Set());
  const onToggleCart = useCallback((deal) => {
    setCartIds(prev => {
      const next = new Set(prev);
      if (next.has(deal.id)) next.delete(deal.id);
      else next.add(deal.id);
      return next;
    });
  }, []);

  return (
    <section style={{ background: SECTION_BG, padding: "8px 0 8px", width: "100%", overflow: "hidden" }}>
      <style>{`.jd-track::-webkit-scrollbar{display:none}.jd-track{-ms-overflow-style:none;scrollbar-width:none}`}</style>
      <DealsRow
        title={<>Pros offering a little <em style={{ fontStyle: "italic", color: GREEN }}>extra</em> this week</>}
        deals={GENERAL_DEALS} onBook={onBook} cartIds={cartIds} onToggleCart={onToggleCart} padX={padX}
      />

      {/* "Home fixes before the rains" — header plus the first three
          sub-rows (Plumbers, Electricians, Gardeners) sit on the same
          dark hero-gradient background used in TreatYourself.jsx.
          Carpenters and Electronics Technicians continue on the light
          background right after, so the section fades back to normal. */}
      <div style={{ background: HERO_BG, padding: "28px 0 8px" }}>
        <div style={{ width: "100%", padding: `0 ${padX}px`, marginBottom: 4 }}>
          <h2 style={{
            fontFamily: SERIF, fontWeight: 400, color: "#fff",
            fontSize: "clamp(30px,4.4vw,42px)", lineHeight: 1.05,
            letterSpacing: "-0.03em", margin: "0 0 20px",
          }}>
            Home fixes before the <em style={{ fontStyle: "italic", color: GREEN_LIGHT }}>rains</em>
          </h2>
        </div>
        <DealsRow
          title="Plumbers"
          deals={PLUMBER_DEALS} onBook={onBook} cartIds={cartIds} onToggleCart={onToggleCart} padX={padX} dark
        />
        <DealsRow
          title="Electricians"
          deals={ELECTRICIAN_DEALS} onBook={onBook} cartIds={cartIds} onToggleCart={onToggleCart} padX={padX} dark
        />
        <DealsRow
          title="Gardeners"
          deals={GARDENER_DEALS} onBook={onBook} cartIds={cartIds} onToggleCart={onToggleCart} padX={padX} marginBottom={0} dark
        />
      </div>

      <div style={{ paddingTop: 28 }}>
        <DealsRow
          title="Carpenters"
          deals={CARPENTER_DEALS} onBook={onBook} cartIds={cartIds} onToggleCart={onToggleCart} padX={padX}
        />
        <DealsRow
          title="Electronics Technicians"
          deals={ELECTRONICS_DEALS} onBook={onBook} cartIds={cartIds} onToggleCart={onToggleCart} padX={padX} marginBottom={0}
        />
      </div>
    </section>
  );
}